/**
 * Client Error Telemetry
 *
 * Reports failures from the library's silent catch blocks.
 *
 * Why this exists: `appendParams`, `handleAffiliateActivation` and
 * `ensureRequestId` all swallow exceptions, deliberately, so a fault can never
 * block a user's navigation. The cost of that safety was a production failure
 * nobody could see — clicks arriving at Tune with no attribution params, on real
 * devices where the library had loaded and every other script on the page ran
 * fine. There was no console error, no log, nothing. This module closes that
 * gap.
 *
 * Lives in the core bundle on purpose. The faults it reports happen before, or
 * instead of, any lazy chunk resolving, so it cannot be one.
 *
 * Constraints this module holds to:
 *  - Never throws. It sits on the navigation path and on the failure path of
 *    the thing it reports; a fault here would compound the original problem.
 *  - Uses `navigator.sendBeacon`, which is specified to survive the page
 *    unloading. A plain `fetch` is cancelled by the navigation a click starts,
 *    which is exactly when we most need the report.
 *  - Sends parameter NAMES, never values. `clreqid`, `tblci` and `aff_unique1`
 *    are pseudonymous identifiers and URLs are logged broadly by
 *    intermediaries.
 *
 * @module telemetry
 */
import { TELEMETRY_BEACON_RELATIVE_PATH, TELEMETRY_SAMPLE_RATE, TELEMETRY_MAX_PER_PAGE, TELEMETRY_MAX_MESSAGE_LENGTH, TELEMETRY_MAX_STACK_LENGTH, TELEMETRY_MAX_PARAM_NAMES_LENGTH, TELEMETRY_MAX_PARAM_VALUES_LENGTH, TELEMETRY_VALUE_ALLOWLIST, TELEMETRY_DISABLE_FLAG, TELEMETRY_SELF_TEST_PARAM, TELEMETRY_DEDUPE_WINDOW_MS, } from '../../config/library/telemetry.config';
/**
 * Build identity, read defensively.
 *
 * DefinePlugin replaces these identifiers with literals, so the guard folds away
 * in a real build. Reading them bare would throw a ReferenceError anywhere the
 * define is absent (tests, or a future build path that forgets it) — and this
 * module's own catch would swallow that, silently disabling all reporting. A
 * `typeof` check on an undeclared identifier is safe by specification.
 */
const buildVersion = typeof BUILD_VERSION !== 'undefined' ? BUILD_VERSION : 'unknown';
const buildTarget = typeof BUILD_TARGET !== 'undefined' ? BUILD_TARGET : 'unknown';
let beaconCount = 0;
/** Key of the last reported error, for collapsing multi-layer duplicates. */
let lastReportKey = '';
/** When the last report was sent, in ms since the epoch. */
let lastReportAt = 0;
/**
 * Resolves the beacon endpoint from the library's own script URL, so the beacon
 * follows whichever mount served the library and is same-origin when the
 * library is served first-party.
 *
 * Captured at module evaluation: `document.currentScript` is only set while the
 * script is executing.
 */
const beaconUrl = (() => {
    try {
        const script = document.currentScript;
        const src = (script === null || script === void 0 ? void 0 : script.src) ||
            // Fallback for async/injected loads where currentScript is null.
            Array.from(document.getElementsByTagName('script'))
                .map(s => s.src)
                .find(s => s && s.indexOf('cl-attr') !== -1);
        if (!src)
            return null;
        return new URL(TELEMETRY_BEACON_RELATIVE_PATH, src).toString();
    }
    catch (_e) {
        return null;
    }
})();
/** Monotonic-ish page age, used to see how a fault relates to load timing. */
function msSinceStart() {
    try {
        return Math.round(performance.now());
    }
    catch (_e) {
        return -1;
    }
}
/** Whether localStorage is actually usable, not merely present. */
function localStorageAvailable() {
    try {
        const probe = '__cl_attr_probe__';
        localStorage.setItem(probe, '1');
        localStorage.removeItem(probe);
        return true;
    }
    catch (_e) {
        return false;
    }
}
/**
 * Extracts the parameter names from an href. Names only — never values.
 *
 * @param href - The anchor href, if available
 * @returns Comma-joined parameter names, or null
 */
function paramNames(href) {
    if (!href)
        return null;
    try {
        const keys = [];
        new URL(href).searchParams.forEach((_value, key) => {
            keys.push(key);
        });
        // Capped: an oversized beacon URL fails silently, which is the failure mode
        // this telemetry exists to eliminate.
        return keys.join(',').slice(0, TELEMETRY_MAX_PARAM_NAMES_LENGTH);
    }
    catch (_e) {
        return null;
    }
}
/**
 * Whether a report should be sent. Split out so the reporting path stays small
 * and each guard is individually obvious.
 *
 * @returns True when a beacon may be sent
 */
function reportingAllowed() {
    const globals = window;
    if (globals[TELEMETRY_DISABLE_FLAG] === true)
        return false;
    if (!beaconUrl)
        return false;
    if (beaconCount >= TELEMETRY_MAX_PER_PAGE)
        return false;
    if (TELEMETRY_SAMPLE_RATE < 1 && Math.random() > TELEMETRY_SAMPLE_RATE) {
        return false;
    }
    return typeof navigator.sendBeacon === 'function';
}
/**
 * Builds the beacon query string.
 *
 * Compact keys keep the URL short: these values travel in the query so they
 * land in zone HTTP analytics, which attaches User-Agent, ASN, country, colo
 * and bot score for free — the correlation the payload itself cannot carry.
 *
 * @param phase - Where the failure happened
 * @param err - Loosely-typed view of the thrown value
 * @param context - Extra diagnostic context
 * @returns Query parameters for the beacon
 */
function paramValues(href) {
    if (!href)
        return '';
    try {
        const allowed = TELEMETRY_VALUE_ALLOWLIST;
        const params = new URL(href, window.location.href).searchParams;
        const pairs = [];
        for (const key of allowed) {
            const value = params.get(key);
            if (value !== null && value !== '')
                pairs.push(`${key}=${value}`);
        }
        return pairs.join('|').slice(0, TELEMETRY_MAX_PARAM_VALUES_LENGTH);
    }
    catch (_e) {
        return '';
    }
}
/**
 * Builds the beacon query string.
 *
 * Compact keys keep the URL short: these values travel in the query so they
 * land in zone HTTP analytics, which attaches User-Agent, ASN, country, colo
 * and bot score for free — the correlation the payload itself cannot carry.
 *
 * @param phase - Where the failure happened
 * @param err - Loosely-typed view of the thrown value
 * @param context - Extra diagnostic context
 * @returns Query parameters for the beacon
 */
function collectFields(phase, err, context) {
    var _a;
    const out = new URLSearchParams();
    const set = (key, value) => {
        if (value !== null && value !== undefined && value !== '') {
            out.set(key, value);
        }
    };
    const href = context.href;
    set('p', phase);
    set('n', err === null || err === void 0 ? void 0 : err.name);
    set('m', (_a = err === null || err === void 0 ? void 0 : err.message) === null || _a === void 0 ? void 0 : _a.slice(0, TELEMETRY_MAX_MESSAGE_LENGTH));
    set('k', paramNames(href));
    set('pv', paramValues(href));
    set('h', href ? String(href.length) : null);
    set('t', href ? (href.indexOf('tblci=') !== -1 ? '1' : '0') : null);
    set('i', context.initCompleted === undefined
        ? null
        : context.initCompleted
            ? '1'
            : '0');
    set('rs', document.readyState);
    set('ls', localStorageAvailable() ? '1' : '0');
    set('ms', String(msSinceStart()));
    set('v', buildVersion);
    set('b', buildTarget);
    set('u', window.location.hostname);
    return out;
}
/**
 * True when this error was already reported moments ago.
 *
 * One fault reaches reportClientError from more than one layer — appendParams
 * catches it and reports `linkEnhancement`, then handleAffiliateActivation's
 * outer catch sees the same exception and reports `click`. Both describe the
 * same failure and each spent one of TELEMETRY_MAX_PER_PAGE, so a single broken
 * link crowded out later, different faults. Measured on a live page: one click
 * produced four beacons.
 *
 * Phase is deliberately not part of the key — differing phases is exactly what
 * this collapses. The first report wins, since the inner layer names the more
 * specific site.
 *
 * @param err - Loosely-typed view of the thrown value
 * @returns True when the report should be suppressed
 */
function isDuplicateReport(err) {
    var _a, _b;
    const key = `${(_a = err === null || err === void 0 ? void 0 : err.name) !== null && _a !== void 0 ? _a : ''}|${(_b = err === null || err === void 0 ? void 0 : err.message) !== null && _b !== void 0 ? _b : ''}`;
    const now = Date.now();
    if (key === lastReportKey &&
        now - lastReportAt < TELEMETRY_DEDUPE_WINDOW_MS) {
        return true;
    }
    lastReportKey = key;
    lastReportAt = now;
    return false;
}
/**
 * Reports a library failure. Silent no-op when disabled, sampled out, over the
 * per-page cap, already reported moments ago, or when no endpoint could be
 * resolved.
 *
 * @param phase - Where the failure happened
 * @param error - The thrown value
 * @param context - Optional extra diagnostic context
 */
export function reportClientError(phase, error, context = {}) {
    try {
        if (!reportingAllowed())
            return;
        const err = error;
        if (isDuplicateReport(err))
            return;
        beaconCount++;
        const params = collectFields(phase, err, context);
        // Stack traces go in the body: too long for a URL, and they must stay out
        // of intermediary request logs and zone analytics.
        const body = (err === null || err === void 0 ? void 0 : err.stack)
            ? String(err.stack).slice(0, TELEMETRY_MAX_STACK_LENGTH)
            : '';
        // sendBeacon is specified to survive unload — the click that triggered this
        // is already navigating away, which would cancel a plain fetch.
        navigator.sendBeacon(`${beaconUrl}?${params.toString()}`, body);
    }
    catch (_e) {
        // Reporting must never fail louder than what it reports.
    }
}
/**
 * Sends one synthetic beacon when the self-test parameter is present.
 *
 * Every reporting path sits behind a defensive catch that is hard to trigger
 * from outside, so this is how the chain (library -> beacon -> worker ->
 * Workers Logs) gets proven without waiting for a real production fault.
 * Exported so tests can drive it; also invoked at module load.
 *
 * @returns True if a self-test beacon was attempted
 */
export function maybeSendSelfTestBeacon() {
    try {
        const flag = new URLSearchParams(window.location.search).get(TELEMETRY_SELF_TEST_PARAM);
        if (flag !== '1')
            return false;
        reportClientError('selftest', new Error('cl-attr telemetry self-test'));
        return true;
    }
    catch (_e) {
        return false;
    }
}
// Fires at load, gated on the query parameter so it never runs for real users.
maybeSendSelfTestBeacon();
/**
 * Resets per-page state. Test-only.
 *
 * @internal
 */
export function resetTelemetryForTests() {
    beaconCount = 0;
}
